import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ProductCard } from '../components/ProductCard';
import { EmptyState } from '../components/ui';
import { api } from '../services/api';
import { Product } from '../types';

const PAGE_SIZE = 200;

export const DonatCatalogPage: React.FC = () => {
  const { gameName } = useParams<{ gameName: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  const decodedName = gameName ? decodeURIComponent(gameName) : '';

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await api.getDonatsBySubtitle(decodedName, PAGE_SIZE, 0);
        const donats = res.products.sort((a, b) => a.minPrice - b.minPrice);
        setProducts(donats);
        setTotalProducts(res.total);
        setDisplayName(donats.length > 0 && donats[0].subtitle ? donats[0].subtitle : decodedName);
      } finally {
        setIsLoading(false);
      }
    };
    if (decodedName) {
      setDisplayName(decodedName);
      load();
    }
  }, [decodedName]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await api.getDonatsBySubtitle(decodedName, PAGE_SIZE, products.length);
      const newProducts = res.products.sort((a, b) => a.minPrice - b.minPrice);
      setProducts(prev => [...prev, ...newProducts]);
      setTotalProducts(res.total);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, decodedName, products.length]);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '200px' });
    observerRef.current.observe(node);
  }, [loadMore]);

  return (
    <Layout>
      <div className="px-4 py-6">
        <div className="mb-5">
          <h1 className="text-[22px] font-bold text-text-primary">{displayName}</h1>
          {!isLoading && totalProducts > 0 && (
            <p className="text-sm text-text-secondary mt-1">
              {totalProducts} {totalProducts === 1 ? 'товар' : totalProducts < 5 ? 'товара' : 'товаров'}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square rounded-[14px] bg-gray-200 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  orientation="vertical"
                />
              ))}
            </div>
            {products.length < totalProducts && (
              <div ref={sentinelRef} className="flex justify-center py-6">
                <svg className="animate-spin" width={28} height={28} viewBox="0 0 50 50" fill="none">
                  <circle cx="25" cy="25" r="20" stroke="#E5E7EB" strokeWidth="4" />
                  <path d="M25 5 A20 20 0 0 1 45 25" stroke="#4361EE" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="Нет донатов"
            description="Для этой игры пока нет доступных донатов"
            action={{
              label: 'На главную',
              onClick: () => navigate('/home'),
            }}
          />
        )}
      </div>
    </Layout>
  );
};
